import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icons
const createIcon = (color, label) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background-color: ${color};
      width: 32px;
      height: 32px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      display: flex;
      align-items: center;
      justify-content: center;
      border: 3px solid white;
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
    ">
      <span style="
        transform: rotate(45deg);
        color: white;
        font-weight: bold;
        font-size: 14px;
      ">${label}</span>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

const storeIcon = createIcon('#2563eb', 'L');
const clientIcon = createIcon('#16a34a', 'C');
const driverIcon = createIcon('#f97316', 'E');

// Component to update map center
const MapUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView([center.lat, center.lng], map.getZoom());
    }
  }, [center, map]);
  return null;
};

// Component to fit bounds
const BoundsFitter = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length >= 2) {
      const leafletBounds = L.latLngBounds(bounds.map(b => [b.lat, b.lng]));
      map.fitBounds(leafletBounds, { padding: [50, 50] });
    }
  }, [bounds, map]);
  return null;
};

const LeafletMap = ({
  storeLocation,
  clientLocation,
  driverLocation,
  routeCoordinates,
  center,
  zoom = 14,
  height = '300px',
  showRoute = true
}) => {
  const [route, setRoute] = useState([]);
  
  // Calculate route using OSRM (free routing service)
  useEffect(() => {
    const fetchRoute = async () => {
      if (!showRoute || !storeLocation || !clientLocation) return;
      
      try {
        const start = `${storeLocation.lng},${storeLocation.lat}`;
        const end = `${clientLocation.lng},${clientLocation.lat}`;
        
        const response = await fetch(
          `https://router.project-osrm.org/route/v1/driving/${start};${end}?overview=full&geometries=geojson`
        );
        const data = await response.json();
        
        if (data.routes && data.routes[0]) {
          const coords = data.routes[0].geometry.coordinates.map(c => ({
            lat: c[1],
            lng: c[0]
          }));
          setRoute(coords);
        }
      } catch (error) {
        console.error('Error fetching route:', error);
      }
    };
    
    fetchRoute();
  }, [storeLocation, clientLocation, showRoute]);

  // Determine map center
  const mapCenter = center || driverLocation || storeLocation || clientLocation || { lat: -23.5505, lng: -46.6333 };
  
  // Collect all points for bounds
  const allPoints = [
    storeLocation,
    clientLocation,
    driverLocation
  ].filter(Boolean);

  return (
    <div style={{ height, width: '100%', borderRadius: '12px', overflow: 'hidden' }}>
      <MapContainer
        center={[mapCenter.lat, mapCenter.lng]}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {allPoints.length >= 2 && <BoundsFitter bounds={allPoints} />}
        {driverLocation && <MapUpdater center={driverLocation} />}
        
        {/* Store marker */}
        {storeLocation && (
          <Marker position={[storeLocation.lat, storeLocation.lng]} icon={storeIcon}>
            <Popup>📦 Loja</Popup>
          </Marker>
        )}
        
        {/* Client marker */}
        {clientLocation && (
          <Marker position={[clientLocation.lat, clientLocation.lng]} icon={clientIcon}>
            <Popup>🏠 Entrega</Popup>
          </Marker>
        )}
        
        {/* Driver marker */}
        {driverLocation && (
          <Marker position={[driverLocation.lat, driverLocation.lng]} icon={driverIcon}>
            <Popup>🛵 Entregador</Popup>
          </Marker>
        )}
        
        {/* Route line */}
        {route.length > 0 && (
          <Polyline
            positions={route.map(c => [c.lat, c.lng])}
            color="#2563eb"
            weight={4}
            opacity={0.8}
          />
        )}
      </MapContainer>
    </div>
  );
};

export default LeafletMap;
